<template>
  <div class="w-full h-[100px] pr-5 py-2.5 border-t border-[#EFEFEF]">
    <div class="flex items-center justify-between">
      <div
        v-if="infringement.copyrights.length === 1"
        class="flex items-center gap-x-1.5"
      >
        <div
          class="font-secondary font-medium text-[14px] leading-[18px]"
          :class="textClasses"
        >
          {{ infringement.matches?.score }}%
        </div>

        <div class="w-[100px] h-2 bg-gray-200 rounded-md">
          <div
            class="h-full rounded-md"
            :class="bgClass"
            :style="{
              width: `${Number(infringement.matches?.score)}%`,
            }"
          ></div>
        </div>
      </div>

      <div
        v-else
        class="font-secondary font-medium text-[14px] leading-[18px] text-gray-800"
      >
        {{ infringement.copyrights.length }} flags
      </div>

      <div class="flex items-center gap-x-[13px]">
        <Pill
          v-if="infringement.label === InfringementLabel.Report"
          class="flex items-center"
          success
        >
          <Icon name="File" :size="10" class="stroke-green-900 mr-[3.5px]" />
          Report
        </Pill>

        <Pill v-else class="flex items-center" warning>
          <Icon name="Alert" :size="10" class="stroke-yellow-700 mr-[3.5px]" />
          Flagged
        </Pill>

        <Icon
          :name="icon"
          :size="13"
          class="stroke-gray-800"
          :class="iconClasses"
        />
      </div>
    </div>

    <div class="flex flex-col gap-y-1">
      <div class="flex items-center gap-x-3">
        <ThumbnailIcon
          v-if="infringement.copyrights.length === 1"
          class="min-w-[24px] max-w-[24px] h-[24px] bg-gray-200/30 rounded-sm p-0.5"
          :type="infringement.copyrights[0].type"
          :image-url="infringement.copyrights[0].asset?.previewImageUrl"
          :icon-size="22"
        />

        <div
          v-else
          class="min-w-[24px] max-w-[24px] h-[24px] bg-gray-200/30 rounded-sm p-0.5 relative"
        >
          <ThumbnailIcon
            v-for="(copyright, index) in infringement.copyrights.slice(0, 3)"
            class="w-5 h-5 absolute"
            :style="getStackedThumbnailStyles(index)"
            :key="copyright.id"
            :type="copyright.type"
            :image-url="copyright.asset?.previewImageUrl"
            :icon-size="22"
          />
        </div>

        <div
          v-truncate
          class="w-full font-secondary text-[14px] leading-[18px] text-gray-800"
        >
          <div v-if="infringement.copyrights.length === 1" data-truncate-target>
            {{ infringement.copyrights[0].name }}
          </div>

          <template v-else
            >{{ infringement.copyrights.length }} copyrights</template
          >
        </div>
      </div>

      <div
        class="shdw min-w-4 max-w-4 h-4 bg-white rounded-full flex items-center justify-center border border-black/8 -my-2.5 ml-1 z-10"
      >
        <Icon
          id="scaling-stroke"
          name="Sort"
          :size="12"
          class="stroke-gray-800"
        />
      </div>

      <div class="flex items-center gap-x-3">
        <ThumbnailIcon
          class="min-w-[24px] max-w-[24px] h-[24px] bg-gray-200/30 rounded-sm p-0.5"
          :type="infringement.infringingFile.type"
          :image-url="infringement.infringingFile.asset.previewImageUrl"
          :icon-size="22"
        />

        <div
          v-truncate
          class="w-full font-secondary text-[14px] leading-[18px] text-gray-800"
        >
          <div data-truncate-target>
            {{ infringement.infringingFile.name }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  InfringementLabel,
  ScoreLevel,
  type InfringementListQuery,
} from '@/shared/graphql/router/graphql'
import { Icon, ThumbnailIcon, type IconName, Pill } from '@dacr/ui'
import { computed } from 'vue'

type InfringementNode =
  InfringementListQuery['infringements']['edges'][number]['node']

const props = defineProps<{
  infringement: InfringementNode
}>()

const textClasses = computed(() => ({
  'text-green-500': props.infringement.matches?.level === ScoreLevel.Low,
  'text-yellow-500': props.infringement.matches?.level === ScoreLevel.Medium,
  'text-red-500': props.infringement.matches?.level === ScoreLevel.High,
}))

const bgClass = computed(() => ({
  'bg-green-500': props.infringement.matches?.level === ScoreLevel.Low,
  'bg-yellow-500': props.infringement.matches?.level === ScoreLevel.Medium,
  'bg-red-500': props.infringement.matches?.level === ScoreLevel.High,
}))

const icon = computed<IconName>(() =>
  props.infringement.copyrights.length > 1 ? 'ChevronUp' : 'ThreeDots'
)

const iconClasses = computed(() => ({
  'rotate-90': props.infringement.copyrights.length > 1,
}))

const getStackedThumbnailStyles = (index: number) => {
  const rotate = index === 0 ? -16 : index === 2 ? 0 : 16

  return {
    transform: `rotate(${rotate}deg)`,
  }
}
</script>

<style scoped>
.shdw {
  box-shadow:
    0 4px 18px 0 rgba(0, 0, 0, 0.04),
    0 0.8px 2.93px 0 rgba(0, 0, 0, 0.02);
}
</style>
